import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X } from "lucide-react";
import { api, formatErr } from "../../lib/api";

const SESSION_KEY = "cft_chat_session";

export default function AIChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm CFT Assistant. Ask me about our services, studio booking, or get a quick quote." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const sessionId = (() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) { s = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(SESSION_KEY, s); }
    return s;
  })();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: formatErr(err.response?.data?.detail) || "Sorry, something went wrong." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-brand bg-[length:200%_auto] animate-gradient-x hover:scale-110 transition shadow-xl grid place-items-center"
        aria-label="Open AI chat"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <Sparkles className="w-6 h-6 text-white" />}
      </button>

      {open && (
        <div data-testid="chatbot-panel"
          className="fixed bottom-24 right-6 z-40 w-[92vw] sm:w-96 h-[28rem] bg-white border border-zinc-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="px-4 py-3 bg-[#0A1128] text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FF7F00]" />
            <div className="font-display font-bold">CFT Assistant</div>
            <span className="ml-auto text-[10px] uppercase tracking-widest text-white/60">AI</span>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#0A1128] text-white rounded-br-sm"
                    : "bg-white border border-zinc-200 text-[#0A1128] rounded-bl-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-zinc-500">CFT Assistant is typing…</div>}
          </div>
          <form onSubmit={send} className="p-2 border-t border-zinc-200 flex gap-2 bg-white">
            <input
              value={input} onChange={(e) => setInput(e.target.value)}
              data-testid="chatbot-input" placeholder="Ask anything…"
              className="flex-1 px-3 py-2 text-sm rounded-full bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#00BFE0]"
            />
            <button type="submit" disabled={busy} data-testid="chatbot-send"
              className="w-10 h-10 grid place-items-center rounded-full bg-gradient-brand text-white disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
